import { Delete } from "@mui/icons-material";
import { Button, styled } from "@mui/material";
import { debounce, get, includes, map } from "lodash";
import { commencementDateTypes } from "../../../app/js/types/commencementDateTypes";
import { propertyManagerTypes } from "../../../app/js/types/propertyManagerTypes";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Add, Search } from "../../../common/js/icons";
import searchConstants from "../search/constants";

const StyledDefTerm = styled("span")({
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#9E9E9E",
    fontWeight: 600,
    display: "inline-block",
    padding: "0 10px 3px",
});

const StyledManagementInfoContainerRow = styled("div")({
    padding: "5px 5px",
});

const StyledMoveItButton = styled(Button)({
    margin: "10px 0 10px 0px",
    paddingTop: "8px",
    "&:hover": {
        background: "transparent",
    },
});

const StyledFlexContainer = styled("div")({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginLeft: "20px",
});

const StyledFlexItem = styled("div")({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

const StyledCommencementWrapper = styled("div")({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px 0px",
});

const StyledManagementContainer = styled("div")({
    border: "1px solid #dbdbdb",
    display: "flex",
    flexDirection: "column",
    height: "100%",
});

const PropertyManagementInformation = (props) => {
    const { autocompleteResults, data, disableInputs, handleDeleteArrayElement, handleInput, searchKMPerson } = props;

    const handleUpdatePersonnelRole = (currentRole, roleIndex, newRole) => {
        const updatedPersonnel = { ...data.personnel };
        const personnelItem = updatedPersonnel[currentRole][roleIndex];
        personnelItem.role = newRole;

        if (!updatedPersonnel[newRole]) {
            updatedPersonnel[newRole] = [];
        }

        updatedPersonnel[newRole].push(personnelItem);
        updatedPersonnel[currentRole].splice(roleIndex, 1);

        if (updatedPersonnel[currentRole].length === 0) {
            delete updatedPersonnel[currentRole];
        }

        handleInput("personnel", updatedPersonnel);
    };

    const handleDeletePersonnel = (role, roleIndex) => {
        handleDeleteArrayElement(`personnel.${role}`, roleIndex);
        const personnel = { ...data.personnel };
        if (personnel[role].length === 1 && roleIndex === 0) {
            delete personnel[role];
            handleInput("personnel", personnel);
        }
    };

    const handleNewPersonnel = () => {
        handleInput("personnel.Select", [{ propertyManagementKey: data?.propertyManagementKey, role: "Select" }]);
    };

    const isAddPersonnelValid = () => {
        const usedPersonnelRoles = new Set(Object.keys(data?.personnel ?? {}));
        return usedPersonnelRoles.has("Select") && data.personnel["Select"].length > 0;
    };

    const getOrderedPersonnel = () => {
        const orderedPersonnel = [];
        if (!("personnel" in data)) return orderedPersonnel;

        const roles = Object.keys(data?.personnel);
        const personnelMap = data?.personnel ?? {};

        roles.forEach((role) => {
            if (role in personnelMap) {
                personnelMap[role].forEach((personnel, index) => {
                    orderedPersonnel.push({ ...personnel, roleIndex: index });
                });
            }
        });
        return orderedPersonnel;
    };

    const getPersonnel = (personnel) => {
        return personnel.person
            ? get(personnel, `person.firstName`, "") + " " + get(personnel, `person.lastName`, "")
            : "";
    };

    const debounceSearchPersonnel = debounce((term) => {
        searchKMPerson(term);
    }, searchConstants["DEBOUNCE_SEARCH_DELAY"]);

    const allowMultiplePersonnel = ["Property Admin", "Operations Manager"];

    return (
        <StyledManagementContainer>
            <div style={{ marginLeft: "8px" }}>
                <h3 style={{ margin: "22px 0px 14px 0px" }}>Management Information</h3>
            </div>

            <StyledCommencementWrapper>
                <div>
                    <StyledDefTerm>Commencement Date:</StyledDefTerm>
                </div>
                <div>
                    <BSInput
                        id="commencementDate"
                        value={data.commencementDate}
                        placeholder="Enter date"
                        errorPlaceholder="hideIt"
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        disabled={disableInputs}
                        noFullWidth={true}
                        type="date"
                        noHelperText={true}
                        extraFormControlStyles={{ marginRight: "8px", marginBottom: "0px" }}
                        extraInputStyles={{ "--bsinput-input-mt": "0px" }}
                    />
                </div>
                <div>
                    <BSSelect
                        id="commencementDateType"
                        value={data.commencementDateType}
                        placeholder="Effective Date"
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        combinedField="labelLessShiftLeft"
                        disabled={disableInputs}
                        noHelperText={true}
                        options={[
                            {
                                value: "",
                                label: "Select",
                                disabled: true,
                            },
                            ...commencementDateTypes,
                        ]}
                        extraFormControlStyles={{ minWidth: "180px", marginBottom: "0px" }}
                        extraInputStyles={{ "--bsselect-input-mt": "0px" }}
                    />
                </div>
            </StyledCommencementWrapper>
            <div
                style={{
                    maxHeight: "220px",
                    overflowX: "hidden",
                }}
            >
                {getOrderedPersonnel().map((element, i) => (
                    <StyledManagementInfoContainerRow key={`Personnel-${i}`}>
                        <StyledFlexContainer>
                            <StyledFlexItem>
                                <div>
                                    <Autocomplete
                                        id="name"
                                        extraFormControlStyles={{ marginBottom: "-1px", marginRight: "8px" }}
                                        initialInputValue={getPersonnel(element)}
                                        itemToString={(i) => (i == null ? "" : i.firstName + " " + i.lastName)}
                                        onSelect={(person) =>
                                            handleInput(
                                                `personnel.${element.role}[${element.roleIndex}].person`,
                                                person
                                            )
                                        }
                                        options={{
                                            key: "person",
                                            display: (suggestion) => {
                                                return suggestion.firstName + " " + suggestion.lastName;
                                            },
                                        }}
                                        placeholder="Enter name"
                                        searchFunction={(input) => debounceSearchPersonnel(input)}
                                        startAdornment={<Search />}
                                        suggestions={autocompleteResults}
                                        disabled={disableInputs}
                                    />
                                </div>
                                <div>
                                    <BSSelect
                                        id="role"
                                        value={element.role === "Select" ? "" : element.role}
                                        placeholder="Select"
                                        onChange={(e) =>
                                            handleUpdatePersonnelRole(element.role, element.roleIndex, e.target.value)
                                        }
                                        combinedField="labelLessShiftLeft"
                                        options={[
                                            {
                                                value: "",
                                                label: "Select",
                                                disabled: true,
                                            },
                                            ...map(propertyManagerTypes, function (p) {
                                                return {
                                                    ...p,
                                                    disabled:
                                                        !includes(allowMultiplePersonnel, p.value) &&
                                                        includes([...Object.keys(get(data, "personnel", {}))], p.value),
                                                };
                                            }),
                                        ]}
                                        extraFormControlStyles={{ marginBottom: "0px", marginLeft: "8px" }}
                                        extraInputStyles={{ "--bsselect-input-mt": "0px" }}
                                    />
                                </div>
                                <div>
                                    <Button
                                        onClick={() => {
                                            handleDeletePersonnel(element.role, element.roleIndex);
                                        }}
                                    >
                                        <Delete />
                                    </Button>
                                </div>
                            </StyledFlexItem>
                        </StyledFlexContainer>
                    </StyledManagementInfoContainerRow>
                ))}
            </div>
            <div style={{ marginTop: "auto" }}>
                {disableInputs !== true && (
                    <StyledMoveItButton
                        color="primary"
                        onClick={() => handleNewPersonnel()}
                        disabled={isAddPersonnelValid()}
                    >
                        <Add fill={isAddPersonnelValid() ? "#DCDCDC" : "#4b94c1"} /> Add Personnel
                    </StyledMoveItButton>
                )}
            </div>
        </StyledManagementContainer>
    );
};

export default PropertyManagementInformation;
